import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, TrendingUpIcon, UsersIcon, LightbulbIcon } from 'lucide-react';
import Button from '../components/Button';
import SkillTag from '../components/SkillTag';
const AboutPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Simulate content loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);
  const workExperience = [{
    title: 'Founder/Product Manager',
    company: 'Stealth Startup',
    period: '2025 - Present',
    description: 'Leading product strategy for an AI-Enhanced Interview platform that helps enterprise customers extract actionable insights from unstructured data.',
    achievements: ['Conducting early-stage product discovery around AI-enhanced interview processes, with a focus on fairness, authenticity, and user trust.', 'Mapping emerging patterns of AI-assisted cheating to inform detection and integrity strategies', 'Designing conceptual MVPs to address top-of-mind concerns for TA leaders and hiring managers in tech-driven organizations']
  }, {
    title: 'Product Manager',
    company: 'Florida Blue',
    period: '2023 - 2024',
    description: 'Managed the development and optimization of Sales Compensation processing systems for Leading Agencies in the Health Insurane Sector.',
    achievements: ['Reduced payment reconciliation discrepancies by $1.1M annually', 'Decreased payment processing time from 23 days to 10 days', 'Implemented automated validation that improved accuracy to 99.6%']
  }, {
    title: 'Senior Product Manager',
    company: 'CVS Health',
    period: '2020 - 2023',
    description: 'Developed Pre and Post care solutions focused on improving care coordination and provider efficiency.',
    achievements: ['Increased provider CSAT from 67% to 92% through UX improvements', 'Reduced documentation time by 35% through workflow optimization', 'Led the implementation of a patient feedback system that improved NPS by 14 points']
  }, {
    title: 'Product Lead',
    company: 'Florida Blue',
    period: '2018 - 2020',
    description: 'Analyzed user behavior data to inform product decisions and optimize conversion funnels.',
    achievements: ['Created dashboards that reduced reporting time by 70%', 'Identified conversion bottlenecks that led to a 28% improvement in sign-up completion', 'Conducted user research that directly informed our product roadmap prioritization']
  }];
  const education = [{
    degree: 'Master of Science',
    institution: 'University of Houston',
    year: '2016',
    focus: 'Technology/Engineering Management'
  }, {
    degree: 'Master of Business Administration',
    institution: 'Symbiosis International Univeristy',
    year: '2013',
    focus: 'Marketing - Advertising'
  }];
  const certifications = ['Certified Scrum Product Owner (CSPO)', 'Google Analytics Advanced Certification', 'AI Product Management (AIPM), Product Faculty', 'Product Management Professional (PMP)'];
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-50" data-id="element-133">
        <div className="animate-pulse flex flex-col items-center" data-id="element-134">
          <div className="h-12 w-12 rounded-full bg-primary-300 mb-4" data-id="element-135"></div>
          <div className="h-4 w-48 bg-gray-200 rounded mb-2" data-id="element-136"></div>
          <div className="h-3 w-32 bg-gray-200 rounded" data-id="element-137"></div>
        </div>
      </div>;
  }
  return <div className="bg-slate-50 w-full" data-id="element-138">
      {/* Hero Section */}
      <section className="bg-white" data-id="element-139">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-id="element-140">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-141">
            <div className="animate-fade-in" data-id="element-142">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900" data-id="element-143">
                About Me
              </h1>
              <p className="mt-4 text-xl text-gray-600" data-id="element-144">
                I'm a Senior Product Manager at a Stealth Startup with 8 years
                of experience building products that solve real problems.
              </p>
              <p className="mt-4 text-gray-600" data-id="element-145">
                My approach combines deep user research, data-driven decision
                making, and strategic business thinking. I specialize in
                leveraging AI to create exceptional user experiences and drive
                measurable business outcomes.
              </p>
              <p className="mt-4 text-gray-600" data-id="element-146">
                Throughout my career, I've consistently delivered products that
                not only delight users but also drive significant business
                results—from increasing retention and revenue to reducing costs
                and improving operational efficiency.
              </p>
              <div className="mt-8" data-id="element-147">
                <Button href="/contact" variant="primary" data-id="element-148">
                  Let's Connect
                </Button>
              </div>
            </div>
            <div className="animate-fade-in" style={{
            animationDelay: '0.2s'
          }} data-id="element-149">
              <img src="https://image2url.com/images/1758821830824-25510891-afe9-48c7-a891-ccdf2f333e3b.jpg" alt="Professional Headshot" className="rounded-lg shadow-lg w-full" loading="eager" data-id="element-150" />
            </div>
          </div>
        </div>
      </section>
      {/* My Approach Section */}
      <section className="py-16 bg-secondary-50" data-id="element-151">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-152">
          <div className="text-center mb-12 animate-on-scroll" data-id="element-153">
            <h2 className="text-3xl font-bold text-secondary-900" data-id="element-154">
              My Approach
            </h2>
            <p className="mt-4 text-xl text-secondary-600 max-w-3xl mx-auto" data-id="element-155">
              How I tackle product challenges and deliver exceptional results.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-156">
            <div className="bg-white p-6 rounded-lg shadow-md animate-on-scroll" data-id="element-157">
              <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4" data-id="element-158">
                <UsersIcon className="h-6 w-6" data-id="element-159" />
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-2" data-id="element-160">
                User-Centered
              </h3>
              <p className="text-secondary-600" data-id="element-161">
                I start with thorough user research to understand pain points
                and needs. This empathy drives all product decisions and ensures
                we build what users actually value.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md animate-on-scroll" style={{
            animationDelay: '0.15s'
          }} data-id="element-162">
              <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4" data-id="element-163">
                <TrendingUpIcon className="h-6 w-6" data-id="element-164" />
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-2" data-id="element-165">
                Data-Driven
              </h3>
              <p className="text-secondary-600" data-id="element-166">
                I leverage analytics to validate hypotheses, measure impact, and
                continuously improve product performance. No decision is made
                without supporting data.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md animate-on-scroll" style={{
            animationDelay: '0.3s'
          }} data-id="element-167">
              <div className="h-12 w-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mb-4" data-id="element-168">
                <LightbulbIcon className="h-6 w-6" data-id="element-169" />
              </div>
              <h3 className="text-xl font-bold text-secondary-900 mb-2" data-id="element-170">
                AI-Native
              </h3>
              <p className="text-secondary-600" data-id="element-171">
                I integrate AI capabilities thoughtfully to solve complex
                problems, enhance user experiences, and create competitive
                advantages in ways that feel natural to users.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Work Experience Section */}
      <section className="py-16 bg-white" data-id="element-172">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-173">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 animate-on-scroll" data-id="element-174">
            Work Experience
          </h2>
          <div className="space-y-12" data-id="element-175">
            {workExperience.map((job, index) => <div key={index} className="border-l-4 border-blue-500 pl-6 animate-on-scroll" style={{
            animationDelay: `${index * 150}ms`
          }} data-id="element-176">
                <div className="flex flex-wrap justify-between items-baseline" data-id="element-177">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-178">
                    {job.title}
                  </h3>
                  <span className="text-sm font-medium text-gray-500" data-id="element-179">
                    {job.period}
                  </span>
                </div>
                <p className="text-lg font-medium text-blue-600 mt-1" data-id="element-180">
                  {job.company}
                </p>
                <p className="mt-4 text-gray-600" data-id="element-181">{job.description}</p>
                <ul className="mt-4 space-y-2" data-id="element-182">
                  {job.achievements.map((achievement, i) => <li key={i} className="flex" data-id="element-183">
                      <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-184" />
                      <span className="text-gray-600" data-id="element-185">{achievement}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>
      {/* Education & Certifications Section */}
      <section className="py-16 bg-slate-50" data-id="element-186">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-188">
            <div className="animate-on-scroll" data-id="element-189">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-190">
                Education
              </h2>
              <div className="space-y-6" data-id="element-191">
                {education.map((edu, index) => <div key={index} className="bg-white p-6 rounded-lg shadow-md" data-id="element-192">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-193">
                      {edu.degree}
                    </h3>
                    <p className="text-blue-600" data-id="element-194">{edu.institution}</p>
                    <div className="flex justify-between mt-2" data-id="element-195">
                      <span className="text-sm text-gray-600" data-id="element-196">
                        Focus: {edu.focus}
                      </span>
                      <span className="text-sm text-gray-600" data-id="element-197">{edu.year}</span>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="animate-on-scroll" style={{
            animationDelay: '0.2s'
          }} data-id="element-198">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-199">
                Certifications
              </h2>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-200">
                <ul className="space-y-3" data-id="element-201">
                  {certifications.map((cert, index) => <li key={index} className="flex items-center" data-id="element-202">
                      <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-203" />
                      <span className="text-gray-700" data-id="element-204">{cert}</span>
                    </li>)}
                </ul>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-6" data-id="element-205">
                Languages
              </h2>
              <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-206">
                <div className="space-y-3" data-id="element-207">
                  <div data-id="element-208">
                    <div className="flex justify-between mb-1" data-id="element-209">
                      <span className="text-gray-700" data-id="element-210">Telugu</span>
                      <span className="text-gray-600" data-id="element-211">Native</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-212">
                      <div className="bg-blue-600 h-2 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-213"></div>
                    </div>
                  </div>
                  <div data-id="element-214">
                    <div className="flex justify-between mb-1" data-id="element-215">
                      <span className="text-gray-700" data-id="element-216">Egnlish</span>
                      <span className="text-gray-600" data-id="element-217">Professional</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-218">
                      <div className="bg-blue-600 h-2 rounded-full" style={{
                      width: '80%'
                    }} data-id="element-219"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-primary-700 text-white" data-id="element-220">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-221">
          <h2 className="text-3xl font-bold animate-on-scroll" data-id="element-222">
            Interested in working together?
          </h2>
          <p className="mt-4 text-xl text-primary-100 max-w-3xl mx-auto animate-on-scroll" style={{
          animationDelay: '0.15s'
        }} data-id="element-223">
            I'm always open to discussing product strategy, development
            challenges, or new opportunities.
          </p>
          <div className="mt-8 animate-on-scroll" style={{
          animationDelay: '0.3s'
        }} data-id="element-224">
            <Button href="/contact" variant="secondary" size="lg" data-id="element-225">
              Contact Me
            </Button>
          </div>
        </div>
      </section>
    </div>;
};
export default AboutPage;